import { z } from 'zod';
import { communicationStatusSchema, safeOperationalReason } from '../mail/communication.js';
import { configFileCategory, isSegregatedCategory } from '../files/categories.js';
import { fileIdSchema } from '../files/stored-file.js';
import { stepStateSchema } from '../steps/contract.js';
import { isAdHocRequest } from '../steps/adhoc.js';
import { auditActionSchema, auditEventFieldsSchema } from './audit.js';
import { foundUsThroughLabel, interviewerSchema } from './person.js';
import { lifecycleAuditDetailSchema } from './status-reasons.js';
import { startOfDayInAgencyZone } from '../time/agency-dates.js';
/**
 * One person's timeline (#62): what happened to them, in order, derived at read time.
 *
 * NOTHING HERE IS STORED. A written timeline row would be a second copy of status, and the
 * first place it disagreed with the audit trail, a Communication or a step instance, nobody
 * could say which one was true. The rows are built from three single-partition reads — the
 * person's audit events, communications and step instances — every time they are asked for.
 *
 * Audit events stay PII-free. What a person reads on a row (a message's subject, a step's
 * heading) is JOINED here from the object the event points at, never copied onto the event:
 * denormalising a subject line into the audit payload would turn the audit container into a
 * second, uncontrolled copy of the personnel file.
 *
 * A Communication or a step instance that no audit event points at still gets a row of its
 * own, so the timeline never hides a message or a request just because its trail predates
 * the event that would have named it.
 */
/**
 * How much of a message's stored text rides on a timeline row (#195). A row is a preview:
 * a page of fifty long letters must not be a megabyte, and the full body is one request away
 * (`GET /api/people/{id}/timeline/messages/{messageId}`), for the one message somebody opens.
 */
export const TIMELINE_MESSAGE_PREVIEW_CHARS = 2000;
/** Rows per timeline page when the caller does not say, and the most it may ask for. */
export const TIMELINE_PAGE_DEFAULT = 50;
export const TIMELINE_PAGE_MAX = 200;
/** A message as the timeline shows it: the stored (sanitised) snapshot and where it stands now. */
export const timelineCommunicationSchema = z
    .object({
    id: z.string().min(1),
    subject: z.string().min(1),
    status: communicationStatusSchema,
    /**
     * The start of the text snapshot exactly as stored — what was, or will be, sent, secrets
     * redacted — cut at `TIMELINE_MESSAGE_PREVIEW_CHARS`. `textTruncated` says whether it was.
     */
    text: z.string().min(1).max(TIMELINE_MESSAGE_PREVIEW_CHARS),
    textTruncated: z.boolean(),
    queuedAt: z.string().datetime(),
    sentAt: z.string().datetime().nullable(),
    /** Why it failed or is waiting, already passed through `safeOperationalReason`. */
    reason: z.string().nullable(),
    redacted: z.boolean(),
})
    .strict();
export const timelineStepSchema = z
    .object({
    id: z.string().min(1),
    /** The PINNED heading: what the person was asked, not what the definition says today. */
    heading: z.string(),
    type: z.string().min(1),
    state: stepStateSchema,
    /**
     * A one-off document request (`isAdHocRequest`), NOT the raw `adHoc` flag: external
     * checkpoints, HR direct records and the offer form set that flag too, and are not requests.
     */
    adHoc: z.boolean(),
    /** Files to the segregated I-9 / health destination. */
    segregated: z.boolean(),
    /**
     * LEGACY: the retained `sharePointItemUrl` of a pre-ADR-009 instance (#61). Null when there
     * is none, or when this viewer may not see a segregated one. Shown as a legacy link only.
     */
    fileUrl: z.string().nullable(),
    /**
     * The committed file (`storedFileId`, ADR-009 §1), opened through the files API. Null when
     * there is none yet, or when this viewer may not see a segregated one.
     */
    storedFileId: fileIdSchema.nullable(),
    /** True when a file exists and was left out for this viewer. */
    fileWithheld: z.boolean(),
})
    .strict();
const at = z.string().datetime();
export const timelineEntrySchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('event'),
        id: z.string().min(1),
        at,
        action: auditActionSchema,
        actorType: auditEventFieldsSchema.shape.actorType,
        subjectType: auditEventFieldsSchema.shape.subjectType,
        /** Field NAMES only, as the audit event stores them. */
        changedFields: z.array(z.string().min(1)),
        from: z.string().nullable(),
        to: z.string().nullable(),
        reason: z.string().nullable(),
        /** A status change's reason code and effective date (#772); null on every other row and on a change that had neither. */
        statusDetail: lifecycleAuditDetailSchema.nullable().optional(),
        communication: timelineCommunicationSchema.nullable(),
        step: timelineStepSchema.nullable(),
    })
        .strict(),
    /**
     * The interview this candidacy began after (#258, #278), derived from `person.intake` rather
     * than stored as its own record. The Add candidate frame tells HR "This goes into their
     * History", and this is what makes that true.
     */
    z.object({
        kind: z.literal('interview'),
        id: z.string().min(1),
        at,
        interviewers: z.array(interviewerSchema),
        foundUsThrough: z.string().nullable(),
    }).strict(),
    z.object({ kind: z.literal('communication'), id: z.string().min(1), at, communication: timelineCommunicationSchema }).strict(),
    z.object({ kind: z.literal('step'), id: z.string().min(1), at, step: timelineStepSchema }).strict(),
]);
export const personTimelineSchema = z
    .object({
    personId: z.string().min(1),
    /** Oldest first, in `compareTimelineEntries` order. */
    entries: z.array(timelineEntrySchema),
})
    .strict();
/**
 * A place in the timeline's order: a row's instant and id. It is what a page cursor carries
 * and all it carries — identifiers and a timestamp, never a subject, a name or a body, because
 * the cursor travels in a URL query. The API wraps it in an opaque, checksummed string.
 */
export const timelinePositionSchema = z.object({ at: z.string().datetime(), id: z.string().min(1).max(256) }).strict();
/**
 * GET /api/people/{id}/timeline — one page, NEWEST first (#195). `nextCursor` asks for the
 * rows older than the last one here; null means this page reached the person's first row.
 */
export const personTimelinePageSchema = z
    .object({
    personId: z.string().min(1),
    entries: z.array(timelineEntrySchema).max(TIMELINE_PAGE_MAX),
    nextCursor: z.string().min(1).max(512).nullable(),
})
    .strict();
/** GET /api/people/{id}/timeline/messages/{messageId} — the whole stored text of one message. */
export const timelineMessageBodySchema = z
    .object({ id: z.string().min(1), text: z.string().min(1), redacted: z.boolean() })
    .strict();
/**
 * The instant an interview row sits at, or null when the date cannot be placed in the agency's
 * zone. See `candidateIntakeSchema.interviewedOn` for why an unplaceable date can exist at all.
 */
function placeInterview(interviewedOn) {
    try {
        return startOfDayInAgencyZone(interviewedOn);
    }
    catch {
        return null;
    }
}
/** The key `referenced` is keyed on: an audit event's subject, by kind, so an email and a step can never collide. */
export function timelineSubjectKey(subjectType, subjectId) {
    return `${subjectType}:${subjectId}`;
}
/**
 * Whether approving this instance stores a segregated file, read off its PINNED config.
 *
 * A step config names its file category under `target` — or, for `hr-record`, under `fileCategory`.
 * ADR-009 renamed only the requirement metadata field; the `target` config key stays, because it is
 * inside every definition's hashed content and renaming it would version every stored definition.
 *
 * Reading ONLY `target` is why this returned false for every hr-record: `direct-record.ts` pins the
 * requirement's `fileCategory`, so an HR direct record of an I-9 or a health requirement was
 * classified unsegregated and `stepView` below emitted its `storedFileId` with
 * `fileWithheld: false`. Both keys are now read in one place — `configFileCategory`.
 */
export function isSegregatedInstance(instance) {
    const category = configFileCategory(instance.config);
    // FAIL CLOSED. A config that names NO category at all used to read as unsegregated, which is the
    // same defect as the misnamed key above, one layer down — and `direct-record.ts` wrote the key
    // only when an upload was attached, so eleven of the thirteen segregated-health requirements
    // produced records with no category whatsoever. An unknown category is not evidence of an
    // ordinary one; the cost of being wrong in this direction is a withheld file id, and in the
    // other it is a medical record.
    return category === null || isSegregatedCategory(category);
}
/** Code-unit order. Never `localeCompare`: the timeline feeds an export, and two machines must agree. */
function codeUnits(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * Oldest first by instant, then by row id. Instants are compared as numbers, not strings:
 * `…:00Z` and `…:00.000Z` are the same moment and would otherwise sort by their punctuation.
 */
export function compareTimelineEntries(a, b) {
    return Date.parse(a.at) - Date.parse(b.at) || codeUnits(a.id, b.id);
}
/** The start of a text, never splitting a surrogate pair. */
function preview(text) {
    if (text.length <= TIMELINE_MESSAGE_PREVIEW_CHARS)
        return { text, truncated: false };
    let cut = text.slice(0, TIMELINE_MESSAGE_PREVIEW_CHARS);
    const last = cut.charCodeAt(cut.length - 1);
    if (last >= 0xd800 && last <= 0xdbff)
        cut = cut.slice(0, -1);
    return { text: cut, truncated: true };
}
function communicationView(record) {
    const body = preview(record.text);
    return {
        id: record.id,
        subject: record.subject,
        status: record.status,
        text: body.text,
        textTruncated: body.truncated,
        queuedAt: record.queuedAt,
        sentAt: record.sentAt,
        reason: safeOperationalReason(record.failureReason ?? record.parkedReason),
        redacted: record.redacted,
    };
}
/**
 * WHETHER THIS VIEWER MAY KNOW THIS FORM EXISTS AT ALL (#383).
 *
 * The answers endpoint 404s a segregated form for a caller without `answers.restricted.read`, and
 * #211's form list omits the row — both so that existence is never confirmed. The timeline did not:
 * it withheld the FILE and still emitted the entry, heading and all. So the endpoint's careful
 * non-disclosure was a local property rather than a system one, and this was the one remaining path
 * by which the fact leaked.
 *
 * `isSegregatedInstance` fails closed on an unknown category, so a form that names no category is
 * withheld rather than assumed ordinary — the same decision ADR-009 Q3 makes about a Hep B
 * declination being as much a medical record as the scanned waiver.
 *
 * This narrows what a viewer sees with no explanation, which is exactly the trade the form list
 * already makes deliberately.
 */
function withheldFrom(instance, viewer) {
    return isSegregatedInstance(instance) && !viewer.canSeeSegregatedFiles;
}
// LEGACY-READ (ADR-009): `sharePointItemUrl` is only on instances filed before files moved to Azure.
function stepView(instance, viewer) {
    const segregated = isSegregatedInstance(instance);
    const withheld = segregated && !viewer.canSeeSegregatedFiles && (instance.sharePointItemUrl !== null || instance.storedFileId !== null);
    return {
        id: instance.id,
        heading: instance.pinned.presentationSnapshot.heading,
        type: instance.type,
        state: instance.state,
        adHoc: isAdHocRequest(instance),
        segregated,
        fileUrl: withheld ? null : instance.sharePointItemUrl,
        storedFileId: withheld ? null : instance.storedFileId,
        fileWithheld: withheld,
    };
}
/** A row's place in the order, with the instant written one way so equal positions encode equally. */
export function timelinePositionOf(entry) {
    return { at: new Date(Date.parse(entry.at)).toISOString(), id: entry.id };
}
/**
 * Every row the sources make, unsorted. `joined` decides which messages and steps are already
 * shown on an event's row: the events in hand for a whole timeline, the whole trail's subjects
 * for a page.
 */
function rows(personId, sources, viewer, joined) {
    // Defensive: every read is single-partition already, but a row from someone else must not
    // be able to ride along on this person's timeline.
    const communications = new Map(sources.communications.filter((c) => c.personId === personId).map((c) => [c.id, c]));
    const instances = new Map(sources.instances.filter((i) => i.personId === personId).map((i) => [i.id, i]));
    const entries = [];
    // An interview with no date has no position in a chronology, so it stays on the person's
    // profile and off the timeline rather than being given an invented time. The id is stable —
    // one interview per person — so paging over it is repeatable.
    const intake = sources.intake;
    if (intake !== undefined && intake.interviewedOn !== null) {
        // NEVER LET ONE ROW COST THE PAGE. `startOfDayInAgencyZone` throws for any day before
        // 1883-11-19 (LMT until then), and this is a READ — so a single bad stored value would make
        // every request for this person's History 500, permanently, with no way back through the UI.
        // `candidateIntakeSchema` now refuses such a date at the write, but a record stored before that
        // guard existed, or arriving by an import path, must still degrade to a missing row. An
        // interview with no date is already left off the chronology; one the chronology cannot place
        // is the same case.
        const at = placeInterview(intake.interviewedOn);
        if (at !== null) {
            entries.push({
                kind: 'interview',
                id: `interview:${personId}`,
                at,
                interviewers: intake.interviewers.map((person) => ({ ...person })),
                // The LABEL, not the stored id (#341). This entry is a projection built for a human to
                // read — the row renders it verbatim — and the id `indeed` is a reporting key, not a
                // sentence. `foundUsThroughLabel` also resolves "Other" to what HR typed, which is not on
                // this entry, and passes a value stored before the list existed through unchanged.
                foundUsThrough: foundUsThroughLabel(intake),
            });
        }
    }
    for (const event of sources.audit) {
        if (event.personId !== personId)
            continue;
        /*
          THESE TWO ACTIONS EXIST ONLY FOR A SEGREGATED FORM (#383), so their presence is the fact
          itself. `step.answers-viewed` is written exclusively inside the handler's `segregated` branch
          — "reading an ordinary form's answers is not an event", as the timeline presenter already
          says — and `step.answers-refused` likewise. Showing either to a viewer who may not read
          segregated answers tells them one exists, which is the disclosure this change closes.
        */
        if (!viewer.canSeeSegregatedFiles && (event.action === 'step.answers-viewed' || event.action === 'step.answers-refused'))
            continue;
        /*
          A SEGREGATED FILE ROW IS THE SAME DISCLOSURE, by a different route (#239, from the #179
          review). Viewers hold `audit.read`, and a `file.viewed` or `file.stored` row carries the
          file's category and `segregated` flag — so a viewer who may not open an I-9 or a health
          record can still read, from the trail, that the person has one.
    
          Only the SEGREGATED rows are withheld. An ordinary file's row stays: unlike the two step
          actions above, a file row's mere existence is not the fact — files are viewed all the time
          — so dropping them all would hide routine history to no purpose.
        */
        if (!viewer.canSeeSegregatedFiles && event.file?.segregated === true)
            continue;
        const communication = event.subjectType === 'email' && event.subjectId !== null ? communications.get(event.subjectId) : undefined;
        const instance = event.subjectType === 'step-instance' && event.subjectId !== null ? instances.get(event.subjectId) : undefined;
        entries.push({
            kind: 'event',
            id: `event:${event.id}`,
            at: event.occurredAt,
            action: event.action,
            actorType: event.actorType,
            subjectType: event.subjectType,
            changedFields: [...event.changedFields],
            from: event.from,
            to: event.to,
            // An empty audit reason is no reason, not a failure to be summarised.
            reason: event.reason.trim() === '' ? null : safeOperationalReason(event.reason),
            statusDetail: event.lifecycleDetail ?? null,
            communication: communication === undefined ? null : communicationView(communication),
            /*
              A segregated instance is WITHHELD, not merely stripped of its file (#383). Its heading and
              type named the form, so an event row carrying it told a viewer a protected form exists.
              The row itself stays — it is a real thing that happened — and names no form.
            */
            step: instance === undefined || withheldFrom(instance, viewer) ? null : stepView(instance, viewer),
        });
    }
    for (const communication of communications.values()) {
        if (joined(timelineSubjectKey('email', communication.id)))
            continue;
        entries.push({ kind: 'communication', id: `communication:${communication.id}`, at: communication.queuedAt, communication: communicationView(communication) });
    }
    for (const instance of instances.values()) {
        if (joined(timelineSubjectKey('step-instance', instance.id)))
            continue;
        // Dropped entirely rather than emitted without its file: a standalone step row IS the form.
        if (withheldFrom(instance, viewer))
            continue;
        entries.push({ kind: 'step', id: `step:${instance.id}`, at: instance.issuedAt, step: stepView(instance, viewer) });
    }
    return entries;
}
function subjectsOf(personId, audit) {
    const keys = new Set();
    for (const event of audit) {
        if (event.personId !== personId || event.subjectId === null)
            continue;
        if (event.subjectType === 'email' || event.subjectType === 'step-instance')
            keys.add(timelineSubjectKey(event.subjectType, event.subjectId));
    }
    return keys;
}
/**
 * Merge the three reads into rows. Pure: no clock, no locale, and the same rows in the same
 * order whatever order the sources arrive in.
 */
export function buildTimeline(personId, sources, viewer) {
    const referenced = subjectsOf(personId, sources.audit);
    const entries = rows(personId, sources, viewer, (key) => referenced.has(key));
    entries.sort(compareTimelineEntries);
    return personTimelineSchema.parse({ personId, entries });
}
/**
 * One page of the same timeline, newest first (#195).
 *
 * Paging is by POSITION, not offset: the rows strictly older than `before`, in
 * `compareTimelineEntries` order, so a row written while somebody reads never shifts a later
 * page, and concatenating every page gives exactly `buildTimeline`'s rows, reversed.
 *
 * `next` is set exactly when a row older than this page's last exists. The audit source only
 * has to hold `limit + 1` rows before the cursor: if an unread audit row were newer than one
 * of the rows kept here, all `limit + 1` read ones would be newer still and the page would
 * already be full of them.
 */
export function buildTimelinePage(personId, sources, viewer, page) {
    const limit = Math.max(1, Math.min(TIMELINE_PAGE_MAX, Math.trunc(page.limit)));
    const { before } = page;
    const candidates = rows(personId, sources, viewer, (key) => sources.referenced.has(key))
        .filter((entry) => before === null || compareTimelineEntries(entry, before) < 0)
        .sort((a, b) => compareTimelineEntries(b, a));
    const entries = candidates.slice(0, limit);
    const last = entries[entries.length - 1];
    const next = candidates.length > limit && last !== undefined ? timelinePositionOf(last) : null;
    return { personId, entries, next };
}
//# sourceMappingURL=timeline.js.map